// =============================================================================
// DateRangePicker.tsx
// Reusable mobile bottom sheet for picking a date range. Six presets ("All
// time", "This month", "Last 30 days", "Last 90 days", "This year", "Custom")
// plus From/To inputs revealed when "Custom" is selected. Apply / Cancel at
// the bottom — Cancel reverts to the last applied selection, Apply commits.
//
// Controlled component: parent owns the `range` state. The picker renders the
// sheet only when `open === true` so it's safe to keep mounted permanently
// alongside the other mobile sheets in MobileSheets.
// =============================================================================

import React, { useEffect, useState } from 'react';

import { S, closeSheetAnim } from '../mobileStyles';

// ── TYPES ────────────────────────────────────────────────────────────

export type DateRangeValue =
  | 'all' | 'this-month' | 'last-30' | 'last-90' | 'this-year' | 'custom';

export interface DateRange {
  value: DateRangeValue;
  from: string;   // YYYY-MM-DD or '' when "all"
  to:   string;   // YYYY-MM-DD or '' when "all"
}

interface DateRangePickerProps {
  open: boolean;
  range: DateRange;
  onApply: (next: DateRange) => void;
  onClose: () => void;
  closingSheet: string | null;
  setClosingSheet: (v: string | null) => void;
}

// ── CONSTANTS ────────────────────────────────────────────────────────

const PRESETS: { value: DateRangeValue; label: string }[] = [
  { value: 'all',        label: 'All time'      },
  { value: 'this-month', label: 'This month'    },
  { value: 'last-30',    label: 'Last 30 days'  },
  { value: 'last-90',    label: 'Last 90 days'  },
  { value: 'this-year',  label: 'This year'     },
  { value: 'custom',     label: 'Custom'        },
];

// ── HELPERS ──────────────────────────────────────────────────────────

const ymd = (d: Date) => d.toISOString().split('T')[0];

export function rangeForPreset(value: DateRangeValue, from = '', to = ''): { from: string; to: string } {
  if (value === 'custom') return { from, to };
  if (value === 'all')    return { from: '', to: '' };
  const now = new Date();
  if (value === 'this-month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: ymd(start), to: ymd(now) };
  }
  if (value === 'this-year') {
    const start = new Date(now.getFullYear(), 0, 1);
    return { from: ymd(start), to: ymd(now) };
  }
  const days = value === 'last-30' ? 30 : 90;
  const start = new Date(now);
  start.setDate(start.getDate() - days);
  return { from: ymd(start), to: ymd(now) };
}

export const labelForRange = (r: DateRange): string => {
  const preset = PRESETS.find(p => p.value === r.value);
  if (r.value === 'custom') {
    if (r.from && r.to) return `${r.from} → ${r.to}`;
    if (r.from)         return `From ${r.from}`;
    if (r.to)           return `Until ${r.to}`;
    return 'Custom';
  }
  return preset?.label || 'All time';
};

// ── MAIN COMPONENT ───────────────────────────────────────────────────

function DateRangePickerImpl({
  open, range, onApply, onClose,
  closingSheet, setClosingSheet,
}: DateRangePickerProps) {

  // Draft state — applied only on Apply. Cancel reverts to `range` prop.
  const [draftValue, setDraftValue] = useState<DateRangeValue>(range.value);
  const [draftFrom,  setDraftFrom]  = useState(range.from);
  const [draftTo,    setDraftTo]    = useState(range.to);

  // Resync when the parent's range changes (e.g. cleared externally) or when
  // the sheet is reopened.
  useEffect(() => {
    if (open) {
      setDraftValue(range.value);
      setDraftFrom(range.from);
      setDraftTo(range.to);
    }
  }, [open, range.value, range.from, range.to]);

  if (!open) return null;

  const handlePreset = (v: DateRangeValue) => {
    setDraftValue(v);
    if (v !== 'custom') {
      const { from, to } = rangeForPreset(v);
      setDraftFrom(from);
      setDraftTo(to);
    }
  };

  const handleApply = () => {
    const final: DateRange = draftValue === 'custom'
      ? { value: 'custom', from: draftFrom, to: draftTo }
      : { value: draftValue, ...rangeForPreset(draftValue) };
    onApply(final);
    closeSheetAnim(setClosingSheet, () => onClose(), 'daterange');
  };

  const handleCancel = () => {
    closeSheetAnim(setClosingSheet, () => onClose(), 'daterange');
  };

  return (
    <div
      style={S.sheet}
      className={closingSheet === 'daterange' ? 'm-sheet-closing' : 'm-sheet-backdrop'}
      onClick={handleCancel}
    >
      <div style={S.sheetContent} className="m-sheet-content" onClick={e => e.stopPropagation()}>
        <div style={S.handle as any} />

        {/* Header */}
        <div style={{
          padding: '8px 20px 12px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: '0.5px solid var(--border)',
        }}>
          <span style={{ fontSize: 17, fontWeight: 600 }}>Date range</span>
          <button
            onClick={handleCancel}
            style={{ fontSize: 'var(--m-text-meta)', color: 'var(--text2)', background: 'none', border: 'none', fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}
          >Cancel</button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px' }}>

          {/* Presets */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {PRESETS.map(p => (
              <button
                key={p.value}
                onClick={() => handlePreset(p.value)}
                className={`filter-chip ${draftValue === p.value ? 'is-active' : ''}`}
                style={{ padding: '8px 14px' }}
              >{p.label}</button>
            ))}
          </div>

          {/* Custom From/To inputs (only when Custom is selected) */}
          {draftValue === 'custom' && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label className="field-label">From</label>
                <input
                  type="date"
                  value={draftFrom}
                  onChange={e => setDraftFrom(e.target.value)}
                  className="input"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="field-label">To</label>
                <input
                  type="date"
                  value={draftTo}
                  onChange={e => setDraftTo(e.target.value)}
                  className="input"
                />
              </div>
            </div>
          )}

          {/* Apply */}
          <button onClick={handleApply} style={S.greenBtn}>Apply</button>
        </div>
      </div>
    </div>
  );
}

export const DateRangePicker = React.memo(DateRangePickerImpl);
